import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import * as api from '../../api';
import Header from '../../components/Header';

const REGIONS = [
  { id: 'gr-ipsc', label: 'Greece' },
  { id: 'it-ipsc', label: 'Italy' },
  { id: 'il-ipsc', label: 'Israel' },
];

export default function CreateLeague({ locale }) {
  const [step, setStep] = useState(1);
  const [matches, setMatches] = useState([]);
  const [pickedMatches, setPickedMatches] = useState([]);
  const [best, setBest] = useState(0);
  const [leagueLocale, setLeagueLocale] = useState('en-US');
  const [leagueAlias, setLeagueAlias] = useState(null);
  const [error, setError] = useState(null);

  async function onSearchMatches({ fromDate, toDate, regionId }) {
    setStep(2);
    setError(null);
    try {
      const [regionRes, matchesRes] = await Promise.all([
        api.getRegion(regionId),
        api.getMatchesPublic(),
      ]);
      const region = regionRes.data;
      const map = matchesRes.data;
      const list = Object.entries(map)
        .map(([id, m]) => ({ id, ...m }))
        .filter((m) => m.countryCode === region.countryCode)
        .filter((m) => {
          if (!m.startDate) return false;
          const d = moment({ year: m.startDate.year, month: m.startDate.month - 1, day: m.startDate.day });
          return d.isAfter(fromDate) && d.isBefore(toDate);
        })
        .sort((a, b) => {
          const an = a.startDate.year * 365 + a.startDate.month * 31 + a.startDate.day;
          const bn = b.startDate.year * 365 + b.startDate.month * 31 + b.startDate.day;
          return bn - an;
        });
      setMatches(list);
      setLeagueLocale(region.locale);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setStep(1);
    }
  }

  function onPickMatches({ ids }) {
    setPickedMatches(ids);
    setBest(ids.length);
    setStep(4);
  }

  function onPickBest({ n }) {
    setBest(n);
    setStep(5);
  }

  async function onPickLeagueName({ title, alias }) {
    setStep(6);
    setError(null);
    const finalAlias = alias || `league-${Math.random().toString(36).substring(2, 10)}`;
    try {
      await api.createLeague({
        title: title || 'New League',
        alias: finalAlias,
        locale: leagueLocale,
        matches: pickedMatches,
        scoring: { type: 'best', best: best || pickedMatches.length },
      });
      setLeagueAlias(finalAlias);
      setStep(7);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setStep(5);
    }
  }

  return (
    <>
      <Header locale={locale} />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>Create Your Own League</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select a list of matches and group them into a league — a website showing the
          accumulative results across all the matches for all the shooters in the league.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step < 6 && <PickCriteria onSearchMatches={onSearchMatches} disabled={step > 1} />}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Searching for matches…</Typography>
          </Box>
        )}
        {step >= 3 && step < 6 && <PickMatches matches={matches} onPickMatches={onPickMatches} disabled={step > 3} />}
        {step >= 4 && step < 6 && <PickBest total={pickedMatches.length} onPickBest={onPickBest} disabled={step > 4} />}
        {step >= 5 && step < 6 && <PickLeagueName onPickLeagueName={onPickLeagueName} disabled={step > 5} />}
        {step === 6 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Generating league…</Typography>
          </Box>
        )}
        {step === 7 && <GeneratedLeague alias={leagueAlias} />}
      </Container>
    </>
  );
}

function PickCriteria({ onSearchMatches, disabled }) {
  const [fromDate, setFromDate] = useState(moment().subtract(1, 'year').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [regionId, setRegionId] = useState('il-ipsc');

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="h6">Step 1: Select Matches</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick a date range and a region; you'll choose the specific matches in the next step.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={disabled} />
        <TextField type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={disabled} />
        <Select size="small" value={regionId} onChange={(e) => setRegionId(e.target.value)} disabled={disabled}>
          {REGIONS.map((r) => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
        </Select>
        <Button variant="contained" disabled={disabled} onClick={() => onSearchMatches({ fromDate, toDate, regionId })}>
          Search Matches
        </Button>
      </Box>
    </Paper>
  );
}

function PickMatches({ matches, onPickMatches, disabled }) {
  const [picked, setPicked] = useState([]);

  const toggle = (id) => {
    setPicked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="h6">Step 2: Pick Matches</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Found {matches.length} matches in this date range.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 360, overflowY: 'auto', mb: 2 }}>
        {matches.map((m) => (
          <FormControlLabel
            key={m.id}
            control={<Checkbox checked={picked.includes(m.id)} onChange={() => toggle(m.id)} disabled={disabled} />}
            label={`${m.title}${m.startDate ? ` — ${m.startDate.year}-${String(m.startDate.month).padStart(2, '0')}-${String(m.startDate.day).padStart(2, '0')}` : ''}`}
          />
        ))}
      </Box>
      <Button variant="contained" disabled={disabled || picked.length === 0} onClick={() => onPickMatches({ ids: picked })}>
        Next ({picked.length} selected)
      </Button>
    </Paper>
  );
}

function PickBest({ total, onPickBest, disabled }) {
  const [n, setN] = useState(total);
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="h6">Step 3: Scoring Mode</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Best <strong>N</strong> out of {total} matches will count for each shooter.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography>Best</Typography>
        <TextField type="number" size="small" value={n} onChange={(e) => setN(parseInt(e.target.value, 10) || 0)} disabled={disabled} sx={{ width: 90 }} />
        <Typography>out of {total}</Typography>
        <Button variant="contained" disabled={disabled || n < 1 || n > total} onClick={() => onPickBest({ n })}>Next</Button>
      </Box>
    </Paper>
  );
}

function PickLeagueName({ onPickLeagueName, disabled }) {
  const [title, setTitle] = useState('');
  const [alias, setAlias] = useState('');
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="h6">Step 4: League Name</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
        <Typography>League name:</Typography>
        <TextField size="small" value={title} onChange={(e) => setTitle(e.target.value)} disabled={disabled} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Typography>League URL:</Typography>
        <Typography variant="body2" color="text.secondary">https://{window.location.host}/leagues/</Typography>
        <TextField size="small" value={alias} onChange={(e) => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="my-league" disabled={disabled} />
      </Box>
      <Button sx={{ mt: 2 }} variant="contained" disabled={disabled} onClick={() => onPickLeagueName({ title, alias })}>
        Create League
      </Button>
    </Paper>
  );
}

function GeneratedLeague({ alias }) {
  const url = `/leagues/${alias}`;
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h6">Done!</Typography>
      <Typography sx={{ mt: 1 }}>
        Your league is ready: <RouterLink to={url}>{`${window.location.host}${url}`}</RouterLink>
      </Typography>
    </Paper>
  );
}
